import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Search, Pencil, Trash2, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const PLACEHOLDERS = [
  { key: '{imie}', desc: 'Imie kontaktu' },
  { key: '{nazwisko}', desc: 'Nazwisko kontaktu' },
  { key: '{miasto}', desc: 'Miasto' },
  { key: '{link}', desc: 'Link afiliacyjny' },
  { key: '{produkt_nazwa}', desc: 'Nazwa produktu' },
  { key: '{produkt_opis}', desc: 'Opis produktu' },
  { key: '{unsubscribe_link}', desc: 'Link rezygnacji (wypisanie z bazy)' },
  { key: '{email}', desc: 'Email kontaktu' },
];

const emptyForm = { name: '', type: 'sms', subject: '', body: '', category: '' };

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const params = { search };
      if (typeFilter !== 'all') params.type = typeFilter;
      const r = await api.get('/templates', { params });
      setTemplates(r.data.items);
    } catch {} finally { setLoading(false); }
  }, [search, typeFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };
  const openEdit = (t) => { setForm({ name: t.name, type: t.type, subject: t.subject || '', body: t.body, category: t.category || '' }); setEditId(t.id); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) { await api.put(`/templates/${editId}`, form); toast.success('Szablon zaktualizowany'); }
      else { await api.post('/templates', form); toast.success('Szablon dodany'); }
      setDialogOpen(false); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac szablon?')) return;
    try { await api.delete(`/templates/${id}`); toast.success('Usunieto'); fetch(); }
    catch { toast.error('Blad'); }
  };

  const insertPlaceholder = (key) => {
    setForm(f => ({ ...f, body: f.body + key }));
  };

  const charCount = form.body.length;
  const smsCount = Math.ceil(charCount / 160) || 1;

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Szablony</h1>
          <p className="text-sm text-muted-foreground mt-1">Szablony wiadomosci SMS i email</p>
        </div>
        <Button onClick={openCreate} data-testid="templates-add-button"><Plus className="h-4 w-4 mr-2" />Nowy szablon</Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Szukaj..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card/50" data-testid="templates-search-input" />
        </div>
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="templates-filter-all">Wszystkie</TabsTrigger>
            <TabsTrigger value="sms" data-testid="templates-filter-sms">SMS</TabsTrigger>
            <TabsTrigger value="email" data-testid="templates-filter-email">Email</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Zmienne</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-24">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Brak szablonow. Utworz pierwszy!</TableCell></TableRow>
                ) : templates.map(t => (
                  <TableRow key={t.id} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell><Badge variant="outline" className={t.type === 'sms' ? 'border-primary/30 text-primary' : 'border-[hsl(var(--info))]/30 text-[hsl(var(--info))]'}>{t.type.toUpperCase()}</Badge></TableCell>
                    <TableCell><div className="flex gap-1 flex-wrap">{(t.variables || []).map(v => <Badge key={v} variant="secondary" className="text-xs font-mono">{`{${v}}`}</Badge>)}</div></TableCell>
                    <TableCell className="text-muted-foreground text-xs font-mono">{t.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Edytuj szablon' : 'Nowy szablon'}</DialogTitle>
            <DialogDescription>Utworz szablon SMS lub email z placeholderami</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Nazwa</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="template-form-name" /></div>
              <div className="space-y-2">
                <Label>Typ</Label>
                <Tabs value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <TabsList className="w-full"><TabsTrigger value="sms" className="flex-1">SMS</TabsTrigger><TabsTrigger value="email" className="flex-1">Email</TabsTrigger></TabsList>
                </Tabs>
              </div>
            </div>
            {form.type === 'email' && <div className="space-y-2"><Label>Temat</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="template-form-subject" /></div>}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Tresc wiadomosci</Label>
                {form.type === 'sms' && <span className="text-xs text-muted-foreground font-mono">{charCount}/160 ({smsCount} SMS)</span>}
              </div>
              <Textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={6} placeholder="Czesc {imie}, mamy dla Ciebie swietna oferte..." className="font-mono text-sm" data-testid="template-form-body" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Wstaw placeholder:</Label>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map(p => (
                  <Tooltip key={p.key}>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs font-mono h-7" onClick={() => insertPlaceholder(p.key)} data-testid={`template-placeholder-${p.key}`}>{p.key}</Button>
                    </TooltipTrigger>
                    <TooltipContent>{p.desc}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.name || !form.body} data-testid="template-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
